/**
 * ExpeditionUI.jsx - Expedition selection, progress display, and controls
 */

import React from 'react';
import { resources } from '../../../data/resources';
import { EXPEDITION } from '../../../data/world-map/balance';

// Helper to get resource icon from canonical source
const getResourceIcon = (resourceId) => resources[resourceId]?.icon || '📦';

export function ExpeditionProgress({
  expeditionProgress,
  onCancel,
  showMessage,
  playerLevel = 1
}) {
  if (!expeditionProgress) return null;

  // Get duration config for XP and drops info
  const durationConfig = EXPEDITION.durations.find(d => d.id === expeditionProgress.durationId);
  const levelMultiplier = Math.min(1 + playerLevel * 0.05, EXPEDITION.levelScaleCap);
  const expectedXp = durationConfig ? Math.floor(durationConfig.expReward * levelMultiplier) : 0;
  const resourceEntries = Object.entries(expeditionProgress.expectedRewards || {});

  return (
    <div className="wm-action-group wm-expedition-active">
      <div className="wm-expedition-progress-layout">
        {/* Header with timer */}
        <div className="wm-expedition-progress-header">
          <span className="wm-expedition-icon">🏕️</span>
          <span className="wm-expedition-timer-large">{expeditionProgress.formattedRemaining}</span>
          <button
            className="wm-exp-cancel-btn"
            onClick={() => {
              const r = onCancel?.();
              if (r?.success) showMessage('Expedition cancelled', true);
            }}
            title="Cancel expedition"
          >
            ✕
          </button>
        </div>

        {/* Progress bar */}
        <div className="wm-expedition-progress-bar">
          <div
            className="wm-expedition-progress-fill"
            style={{ width: `${(expeditionProgress.progress || 0) * 100}%` }}
          />
        </div>

        {/* Resources row */}
        <div className={`wm-resource-list count-${Math.min(resourceEntries.length, 6)}`}>
          {resourceEntries.map(([r, a]) => (
            <span key={r} className="wm-res-tag">+{a} {getResourceIcon(r)}</span>
          ))}
        </div>

        {/* XP and drops row */}
        <div className="wm-expedition-preview-header">
          <span className="wm-exp-xp-tag">⭐ +{expectedXp} XP</span>
          {durationConfig && (
            <span className="wm-exp-drops-tag">📦 {durationConfig.dropCount[0]}-{durationConfig.dropCount[1]} drops</span>
          )}
        </div>
      </div>
    </div>
  );
}

export function ExpeditionSelector({
  currentNode,
  selectedDuration,
  setSelectedDuration,
  getExpeditionDurations,
  calculateExpeditionRewards,
  startMapExpedition,
  showMessage,
  triggerTutorial,
  playerLevel = 1
}) {
  const durations = getExpeditionDurations?.() || [];

  return (
    <div className="wm-action-group wm-expedition-section">
      <div className="wm-action-label">🏕️ Expedition</div>
      <div className="wm-expedition-durations">
        {durations.map(d => {
          const rewards = calculateExpeditionRewards?.(currentNode, d.id);
          return (
            <button
              key={d.id}
              className={`wm-expedition-btn ${selectedDuration === d.id ? 'selected' : ''}`}
              onClick={() => {
                setSelectedDuration(selectedDuration === d.id ? null : d.id);
                triggerTutorial?.('firstExpedition');
              }}
              title={`${d.label}: +${d.expReward} XP, ${d.dropCount[0]}-${d.dropCount[1]} items`}
            >
              <span className="wm-exp-label">{d.label}</span>
              <span className="wm-exp-mult">x{d.multiplier}</span>
            </button>
          );
        })}
      </div>
      {selectedDuration && (() => {
        const rewards = calculateExpeditionRewards?.(currentNode, selectedDuration);
        const durationConfig = durations.find(d => d.id === selectedDuration);
        if (!rewards || !durationConfig) return null;

        // Calculate expected XP (same formula as in GameContext)
        const levelMultiplier = Math.min(1 + playerLevel * 0.05, EXPEDITION.levelScaleCap);
        const expectedXp = Math.floor(durationConfig.expReward * levelMultiplier);

        const resourceEntries = Object.entries(rewards.rewards);

        return (
          <div className="wm-expedition-preview">
            <div className={`wm-resource-list count-${Math.min(resourceEntries.length, 6)}`}>
              {resourceEntries.map(([r, a]) => (
                <span key={r} className="wm-res-tag">+{a} {getResourceIcon(r)}</span>
              ))}
            </div>
            <div className="wm-expedition-preview-header">
              <span className="wm-exp-xp-tag">⭐ +{expectedXp} XP</span>
              <span className="wm-exp-drops-tag">📦 {durationConfig.dropCount[0]}-{durationConfig.dropCount[1]} drops</span>
            </div>
            <button
              className="wm-btn primary"
              onClick={() => {
                const r = startMapExpedition?.(selectedDuration);
                if (r?.success) {
                  showMessage(`Expedition started (${rewards.label})`);
                  setSelectedDuration(null);
                } else {
                  showMessage(r?.error || 'Failed', true);
                }
              }}
            >
              🏕️ Start Expedition
            </button>
          </div>
        );
      })()}
    </div>
  );
}

export default { ExpeditionProgress, ExpeditionSelector };
